import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { CreateDeliverableParams } from "../../../../api/services/programTemplate/models";
import {
  useGetProgramTemplateDeliverableById,
  useUpdateDeliverableMutation,
} from "../../../../api/services/programTemplate/programTemplateApis";
import { MuiDoubleLabel, MuiGrid, MuiModal } from "../../../../components";
import { MuiDoubleLabelWithChip } from "../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import MeasurementInput from "../../../../components/measurementInput/MeasurementInput";
import { MODULE_PERMISSIONS } from "../../../../helpers/constants";
import { UnitTypesKey } from "../../../../helpers/constants/siteCoreKeys";
import { replaceHashWithValue } from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import {
  UpdateDeliverablesFormKey,
  UpdateDeliverablesFormProps,
} from "../../modelsAndSchemas/Models";
import { UpdateDeliverableValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { createProgramSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CancelFieldModal } from "../cancelFieldModal/CancelFieldModal";
import { ViewDeliverablesWrapper } from "./ViewDeliverables.style";

interface ViewDeliverablesProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  selectedDeliverableId: string;
  isViewTemplate?: boolean;
  isEditDeliverable?: boolean;
  editButtonClick?: () => void;
  programTemplateId?: string;
  callApiFunction?: () => void;
  viewTemplateId?: string;
}

export const ViewDeliverables: FC<ViewDeliverablesProps> = ({
  show,
  handleCloseModal,
  pageId,
  selectedDeliverableId,
  isViewTemplate = false,
  isEditDeliverable = false,
  editButtonClick,
  programTemplateId,
  callApiFunction,
  viewTemplateId,
}) => {
  const dispatch = useDispatch();
  const gridSize = { md: 4, sm: 6, xs: 12 };
  const gridRef = useRef<HTMLDivElement>(null);
  const { hasModulePermission } = usePermissions();

  const {
    commonLabels,
    fetchLabel,
    fetchValueByKeyOfDropdown,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    mutateAsync: updateDeliverableApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
  } = useUpdateDeliverableMutation();

  const [showCancel, setShowCancel] = useState(false);

  const {
    data: deliverableData,
    isFetching,
    isError,
    isSuccess,
  } = useGetProgramTemplateDeliverableById(
    viewTemplateId ?? "",
    selectedDeliverableId,
  );

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isFetching || isUpdatePending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isFetching, isUpdatePending]);

  useEffect(() => {
    if (isError || isUpdateError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isError, isUpdateError]);

  useEffect(() => {
    if (isUpdateSuccess) {
      dispatch(
        toggleToast({
          open: isUpdateSuccess,
          message: getLabel(createProgramSiteCoreKeys.deliverableUpdated),
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
      if (callApiFunction) {
        callApiFunction();
      }
      handleCloseModal();
    }
  }, [isUpdateSuccess]);

  const getLabel = (key: string) => {
    return fetchLabel(createProgramSiteCoreKeys.deliverablesListKey, key);
  };

  const getUnitText = () => {
    if (deliverableData?.units === UnitTypesKey.Minutes) {
      return createProgramSiteCoreKeys.createDeliverableMinutes;
    } else if (deliverableData?.units === UnitTypesKey.Count) {
      return createProgramSiteCoreKeys.createDeliverableQuantity;
    } else if (deliverableData?.units === UnitTypesKey.Days) {
      return createProgramSiteCoreKeys.createDeliverableNumberOfDays;
    } else {
      return createProgramSiteCoreKeys.halfDay;
    }
  };

  const getMeasurementValue = () => {
    if (!deliverableData?.units) {
      return "";
    }
    if (deliverableData.units === UnitTypesKey.Minutes) {
      return fetchValueByKeyOfDropdown(
        createProgramSiteCoreKeys.createDeliverableKey,
        createProgramSiteCoreKeys.createDeliverableMinuteList,
        deliverableData.measurement,
      );
    }
    return deliverableData.measurement;
  };

  const activityTypes = useMemo(() => {
    return Array.isArray(deliverableData?.activityTypes)
      ? deliverableData?.activityTypes.map((data: any) => ({
          value: data.id,
          label: data.name,
        }))
      : [];
  }, [deliverableData]);

  const unitValue = fetchValueByKeyOfDropdown(
    createProgramSiteCoreKeys.createDeliverableKey,
    createProgramSiteCoreKeys.createDeliverableUnitList,
    deliverableData?.units ?? "",
  );

  const defaultValues = {
    [UpdateDeliverablesFormKey.units]:
      deliverableData?.units || UnitTypesKey.Minutes,
    [UpdateDeliverablesFormKey.measurementMinute]: "",
    [UpdateDeliverablesFormKey.measurementOther]: null,
  };

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { isDirty: isDirtyForm },
  } = useForm<UpdateDeliverablesFormProps>({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      UpdateDeliverableValidationSchema(commonValidationMessages) as any,
    ),
  });

  useEffect(() => {
    if (isSuccess && isEditDeliverable) {
      setValue(
        deliverableData.units === UnitTypesKey.Minutes
          ? UpdateDeliverablesFormKey.measurementMinute
          : UpdateDeliverablesFormKey.measurementOther,
        deliverableData?.measurement,
      );
      setValue(UpdateDeliverablesFormKey.units, deliverableData?.units);
    }
  }, [deliverableData]);

  const onSubmitClick = () => {
    const { measurementMinute, measurementOther } = getValues();

    let measurement: string;

    const units = deliverableData?.units;

    if (units === UnitTypesKey.Minutes) {
      measurement = measurementMinute;
    } else if (units === UnitTypesKey.Count || units === UnitTypesKey.Days) {
      measurement = (measurementOther as number).toString();
    } else {
      measurement = "1";
    }

    const params: CreateDeliverableParams = {
      name: deliverableData?.name,
      deliverableType: deliverableData?.deliverableType,
      units,
      measurement,
      localizedDeliverableNames: [],
    };
    if (programTemplateId) {
      updateDeliverableApi({
        params,
        programTemplateId,
        deliverableId: selectedDeliverableId,
      });
    }
    //  and what should be the case when halfday in this scenario
  };

  const handleSecondayButtonClick = () => {
    if (isEditDeliverable && isDirtyForm) {
      setShowCancel(true);
    } else {
      handleCloseModal();
    }
  };

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    handleCloseModal();
  };

  const closeModalHandler = () => setShowCancel(false);

  const onUpdateBtnClick = () => {
    if (isEditDeliverable) handleSubmit(onSubmitClick)();
  };

  return (
    <MuiModal
      open={show}
      onClose={handleCloseModal}
      title={
        isEditDeliverable
          ? replaceHashWithValue(
              getLabel(createProgramSiteCoreKeys.editDeliverableTitle),
              deliverableData?.name ?? "",
            )
          : replaceHashWithValue(
              getLabel(createProgramSiteCoreKeys.viewDeliverable),
              deliverableData?.name ?? "",
            )
      }
      primaryButtonTitle={isEditDeliverable ? commonLabels.update : ""}
      onPrimaryButtonClick={() => onUpdateBtnClick()}
      secondaryButtonTitle={
        isEditDeliverable ? commonLabels.cancel : commonLabels.close
      }
      onSecondaryButtonClick={handleSecondayButtonClick}
      tertiaryButtonTitle={
        !isViewTemplate &&
        hasModulePermission(MODULE_PERMISSIONS.EDIT_ACTIVITIES_AND_DELIVERABLES)
          ? getLabel(createProgramSiteCoreKeys.editDeliverableButtonText)
          : ""
      }
      onTertiaryButtonClick={editButtonClick}
      hasLargeContent
      stackButtonsOnMobile={!isEditDeliverable}
    >
      <ViewDeliverablesWrapper>
        <MuiGrid container data-testid={`${pageId}-view-deliverables`}>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel(createProgramSiteCoreKeys.deliverableViewType)}
              value={fetchValueByKeyOfDropdown(
                createProgramSiteCoreKeys.createDeliverableKey,
                createProgramSiteCoreKeys.deliverableTypeList,
                deliverableData?.deliverableType ?? "",
              )}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel(createProgramSiteCoreKeys.deliverablesViewName)}
              value={deliverableData?.name ?? ""}
              isValueWithEllipsis
              valueClasses="deliverable-value"
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabelWithChip
              label={getLabel(
                createProgramSiteCoreKeys.deliverablesActivityType,
              )}
              replaceEmptyWithDash={!activityTypes.length}
              value={activityTypes}
              chipPopoverGridRef={gridRef}
              chipPopoverHeaderLabel={getLabel(
                createProgramSiteCoreKeys.selectedActivities,
              )}
              showMoreARIALabel={commonARIALabel.showNMore}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel(createProgramSiteCoreKeys.deliverablesUnits)}
              value={unitValue}
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            {isEditDeliverable ? (
              <MeasurementInput
                selectedUnit={deliverableData?.units}
                measurementOtherMinuteName={
                  UpdateDeliverablesFormKey.measurementMinute
                }
                measurementOther={UpdateDeliverablesFormKey.measurementOther}
                control={control}
                pageId={pageId}
                parentKey={createProgramSiteCoreKeys.createDeliverableKey}
                minutesLabelKey={
                  createProgramSiteCoreKeys.createDeliverableMinutes
                }
                quantityLabelKey={
                  createProgramSiteCoreKeys.createDeliverableQuantity
                }
                daysLabelKey={
                  createProgramSiteCoreKeys.createDeliverableNumberOfDays
                }
                minuteListKey={
                  createProgramSiteCoreKeys.createDeliverableMinuteList
                }
              />
            ) : (
              <MuiDoubleLabel
                className="measurement-double-label"
                label={fetchLabel(
                  createProgramSiteCoreKeys.createDeliverableKey,
                  getUnitText(),
                )}
                value={getMeasurementValue()}
              />
            )}
          </MuiGrid>
        </MuiGrid>

        {showCancel && (
          <CancelFieldModal
            showModal={showCancel}
            handleModalClose={closeModalHandler}
            handlePrimaryButtonClick={cancelConfirmHandler}
          />
        )}
      </ViewDeliverablesWrapper>
    </MuiModal>
  );
};
